import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { Pitch, AppNotification, Stage, Meeting, DiligenceData, AnalysisScores } from '@/types';
import { DEFAULT_MILESTONES, ANALYSIS_THRESHOLD } from '@/data/mockData';
import { generateBootcampDays } from '@/data/bootcampStructure';
import { analyzePitch } from '@/services/aiAnalysis';
import { supabase } from '@/lib/supabaseClient';
import { triggerGlobalRefresh } from '@/lib/eventBus';

interface DataState {
  pitches: Pitch[];
  notifications: AppNotification[];
  globalRefreshKey: number;
  triggerGlobalRefresh: () => void;
  createPitch: (pitch: Omit<Pitch, 'id' | 'stage' | 'analysisScores' | 'meetings' | 'diligenceData' | 'contractSignedFounder' | 'contractSignedAnalyst' | 'bootcampProgress' | 'milestones' | 'escrowBalance' | 'assignedAnalystId' | 'assignedBySeniorId' | 'deletedAt' | 'createdAt' | 'updatedAt'>) => Promise<string>;
  updatePitchFields: (id: string, fields: Partial<Pick<Pitch, 'companyName' | 'tagline' | 'problem' | 'solution' | 'marketSize' | 'businessModel' | 'team'>>) => Promise<void>;
  submitForAnalysis: (pitchId: string) => Promise<void>;
  assignPitch: (pitchId: string, analystId: string, seniorId?: string) => Promise<void>;
  updateMeetingAgenda: (pitchId: string, meetingId: string, agenda: string[], founderId: string) => Promise<void>;
  approveMeeting: (pitchId: string, meetingId: string) => Promise<void>;
  rejectMeeting: (pitchId: string, meetingId: string) => Promise<void>;
  saveMeeting: (pitchId: string, meetingData: any) => Promise<void>;
  saveMeetingNotes: (pitchId: string, meetingId: string, notes: any) => Promise<void>;
  updateMeetingStatus: (pitchId: string, meetingId: string, status: string) => Promise<void>;
  getMeetingsForPitch: (pitchId: string) => any[];
  submitDiligence: (pitchId: string, data: Omit<DiligenceData, 'verified'>) => Promise<void>;
  verifyDiligence: (pitchId: string) => Promise<void>;
  signContract: (pitchId: string, signer: 'founder' | 'analyst') => Promise<void>;
  toggleBootcampDay: (pitchId: string, dayIndex: number) => Promise<void>;
  completeMilestone: (pitchId: string, milestoneId: string) => Promise<void>;
  approveMilestone: (pitchId: string, milestoneId: string) => Promise<void>;
  markNotificationRead: (notificationId: string) => void;
  markAllNotificationsRead: (userId: string) => void;
  clearNotificationsForPitch: (pitchId: string, tab?: string) => void;
  getNotificationsForUser: (userId: string) => AppNotification[];
  deletePitch: (pitchId: string, userId: string) => Promise<void>;
  adminDeletePitch: (pitchId: string) => Promise<void>;
  restorePitch: (pitchId: string, userId: string) => Promise<boolean>;
  permanentlyDeletePitch: (pitchId: string, userId: string) => Promise<boolean>;
  isDeletable: (pitch: Pitch, userId: string) => boolean;
  getUnreadCount: (userId: string) => number;
  saveTempPitch: (data: any) => void;
  getTempPitch: () => any;
  clearTempPitch: () => void;
  updatePitchStage: (pitchId: string, newStage: Stage) => Promise<void>;
  refreshPitches: () => Promise<void>;
  isSupabaseConnected: boolean;
}

const DataContext = createContext<DataState | null>(null);

export function useData() {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error('useData must be inside DataProvider');
  return ctx;
}

function generateAgenda(pitch: Pitch): string[] {
  const agenda = [
    `Review company overview: ${pitch.companyName}`,
    `Problem: ${pitch.problem?.slice(0, 80)}${pitch.problem?.length > 80 ? '...' : ''}`,
    `Solution: ${pitch.solution?.slice(0, 80)}${pitch.solution?.length > 80 ? '...' : ''}`,
    `Market opportunity: ${pitch.marketSize?.slice(0, 60)}${pitch.marketSize?.length > 60 ? '...' : ''}`,
    `Business model: ${pitch.businessModel?.slice(0, 60)}${pitch.businessModel?.length > 60 ? '...' : ''}`,
  ];
  
  if (pitch.traction) {
    agenda.push(`Traction: ${pitch.traction.customerInterviews || 0} customer interviews, ${pitch.traction.waitlistSignups || 0} waitlist signups`);
  }
  
  if (pitch.competitive?.competitiveAdvantage) {
    agenda.push(`Competitive advantage: ${pitch.competitive.competitiveAdvantage.slice(0, 60)}`);
  }
  
  if (pitch.financial?.fundingAsk) {
    agenda.push(`Funding ask: $${pitch.financial.fundingAsk.toLocaleString()}`);
  }
  
  agenda.push(`Team assessment and execution readiness`);
  agenda.push(`Define next steps: contract and due diligence`);
  
  return agenda;
}

// Helper to convert Pitch to Supabase format
const pitchToSupabase = (pitch: Pitch) => ({
  id: pitch.id,
  founder_id: pitch.founderId,
  company_name: pitch.companyName,
  tagline: pitch.tagline,
  problem: pitch.problem,
  solution: pitch.solution,
  market_size: pitch.marketSize,
  business_model: pitch.businessModel,
  team: pitch.team,
  stage: pitch.stage,
  analysis_scores: pitch.analysisScores,
  assigned_analyst_id: pitch.assignedAnalystId,
  deleted_at: pitch.deletedAt,
  created_at: pitch.createdAt,
  updated_at: pitch.updatedAt,
  traction: pitch.traction,
  competitive: pitch.competitive,
  financial: pitch.financial,
  gtm: pitch.gtm,
  ip: pitch.ip,
  risks: pitch.risks,
  contract_signed_founder: pitch.contractSignedFounder,
  contract_signed_analyst: pitch.contractSignedAnalyst,
  bootcamp_progress: pitch.bootcampProgress,
  milestones: pitch.milestones,
  escrow_balance: pitch.escrowBalance,
  diligence_data: pitch.diligenceData,
});

// Helper to convert Supabase row to Pitch
const supabaseToPitch = (row: any): Pitch => ({
  id: row.id,
  founderId: row.founder_id,
  companyName: row.company_name,
  tagline: row.tagline || '',
  problem: row.problem || '',
  solution: row.solution || '',
  marketSize: row.market_size || '',
  businessModel: row.business_model || '',
  team: row.team || '',
  stage: row.stage,
  analysisScores: row.analysis_scores,
  assignedAnalystId: row.assigned_analyst_id,
  assignedBySeniorId: null,
  deletedAt: row.deleted_at,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
  traction: row.traction,
  competitive: row.competitive,
  financial: row.financial,
  gtm: row.gtm,
  ip: row.ip,
  risks: row.risks,
  contractSignedFounder: row.contract_signed_founder || false,
  contractSignedAnalyst: row.contract_signed_analyst || false,
  bootcampProgress: row.bootcamp_progress || [],
  milestones: row.milestones || [],
  escrowBalance: row.escrow_balance || 0,
  diligenceData: row.diligence_data,
  meetings: [],
});

export function DataProvider({ children }: { children: ReactNode }) {
  const [pitches, setPitches] = useState<Pitch[]>([]);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [meetings, setMeetings] = useState<Record<string, any[]>>({});
  const [globalRefreshKey, setGlobalRefreshKey] = useState(0);
  const [isSupabaseConnected, setIsSupabaseConnected] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  const pollingInterval = useRef<NodeJS.Timeout | null>(null);
  const notificationsRef = useRef(notifications);
  const pitchesRef = useRef(pitches);
  
  useEffect(() => { notificationsRef.current = notifications; }, [notifications]);
  useEffect(() => { pitchesRef.current = pitches; }, [pitches]);

  const triggerGlobalRefresh = useCallback(() => {
    setGlobalRefreshKey(prev => {
      const next = prev + 1;
      window.dispatchEvent(new CustomEvent('global-refresh', {
        detail: { timestamp: new Date().toISOString(), key: next }
      }));
      return next;
    });
  }, []);

  const refreshPitches = useCallback(async () => {
    try {
      console.log('🔄 Fetching latest pitches from Supabase...');
      const { data, error } = await supabase
        .from('pitches')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Supabase error:', error.message);
        setIsSupabaseConnected(false);
        
        const stored = localStorage.getItem('vett_pitches');
        if (stored && pitches.length === 0) {
          const localPitches = JSON.parse(stored);
          setPitches(localPitches.filter((p: Pitch) => !p.permanentlyDeleted));
          console.log('📦 Using localStorage fallback - found', localPitches.length, 'pitches');
        }
        return;
      }
      
      setIsSupabaseConnected(true);
      
      if (data) {
        const formattedPitches = data.map(supabaseToPitch);
        setPitches(formattedPitches);
        console.log('✅ Updated', formattedPitches.length, 'pitches from Supabase');
        localStorage.setItem('vett_pitches', JSON.stringify(formattedPitches));
      } else {
        setPitches([]);
        localStorage.setItem('vett_pitches', '[]');
        console.log('📦 No pitches in Supabase, cleared local state');
      }
      
      triggerGlobalRefresh();
    } catch (error) {
      console.error('Refresh failed:', error);
      setIsSupabaseConnected(false);
    } finally {
      setIsInitialLoad(false);
    }
  }, [triggerGlobalRefresh, pitches.length]);

  const refreshPitchesRef = useRef(refreshPitches);
  useEffect(() => { refreshPitchesRef.current = refreshPitches; }, [refreshPitches]);

  useEffect(() => {
    console.log('🚀 INITIAL LOAD: Fetching pitches from Supabase...');
    refreshPitchesRef.current();
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('👁️ Tab focused - refreshing pitches');
        refreshPitchesRef.current();
      }
    };
    
    window.addEventListener('visibilitychange', handleVisibilityChange);
    return () => window.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useEffect(() => {
    console.log('🚀 Setting up polling (every 15 seconds)...');
    refreshPitches();
    pollingInterval.current = setInterval(() => {
      refreshPitchesRef.current();
    }, 15000);
    return () => {
      console.log('🛑 Cleaning up polling interval');
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
        pollingInterval.current = null;
      }
    };
  }, []);

  useEffect(() => {
    console.log('🔌 Setting up WebSocket realtime subscription for pitches...');
    
    const channel = supabase
      .channel('pitches-realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'pitches' },
        (payload) => {
          console.log('📡 WebSocket realtime event:', payload.eventType, payload.new?.id || payload.old?.id);
          refreshPitchesRef.current();
        }
      )
      .subscribe((status) => {
        console.log('🔌 WebSocket connection status:', status);
        setIsSupabaseConnected(status === 'SUBSCRIBED');
      });

    return () => {
      console.log('🔌 Cleaning up WebSocket subscription');
      supabase.removeChannel(channel);
    };
  }, []);

  useEffect(() => {
    if (pitches.length > 0) {
      localStorage.setItem('vett_pitches', JSON.stringify(pitches));
    }
  }, [pitches]);

  useEffect(() => {
    localStorage.setItem('vett_notifications', JSON.stringify(notifications));
  }, [notifications]);

  useEffect(() => {
    localStorage.setItem('vett_meetings', JSON.stringify(meetings));
  }, [meetings]);

  const saveTempPitch = useCallback((data: any) => {
    localStorage.setItem('temp_pitch', JSON.stringify(data));
  }, []);

  const getTempPitch = useCallback(() => {
    const saved = localStorage.getItem('temp_pitch');
    return saved ? JSON.parse(saved) : null;
  }, []);

  const clearTempPitch = useCallback(() => {
    localStorage.removeItem('temp_pitch');
  }, []);

  const addNotification = useCallback((
    userId: string, 
    message: string, 
    pitchId: string, 
    tab?: string,
    type: 'info' | 'success' | 'warning' | 'error' = 'info'
  ) => {
    const exists = notificationsRef.current.some(n => 
      n.userId === userId && 
      n.pitchId === pitchId && 
      n.tab === tab && 
      n.message === message &&
      !n.read
    );
    if (exists) return;
    setNotifications(prev => [{
      id: `notif-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      userId,
      message,
      pitchId,
      tab,
      type,
      read: false,
      createdAt: new Date().toISOString(),
    }, ...prev]);
    triggerGlobalRefresh();
  }, [triggerGlobalRefresh]);

  const clearNotificationsForPitch = useCallback((pitchId: string, tab?: string) => {
    setNotifications(prev => 
      prev.filter(n => {
        if (n.pitchId !== pitchId) return true;
        if (tab && n.tab === tab) return false;
        if (!tab && !n.tab) return false;
        return true;
      })
    );
    triggerGlobalRefresh();
  }, [triggerGlobalRefresh]);

  const isDeletable = useCallback((pitch: Pitch, userId: string): boolean => {
    if (pitch.founderId !== userId) return false;
    if (pitch.deletedAt) return false;
    const nonDeletableStages: Stage[] = ['validation', 'contract', 'bootcamp', 'diligence', 'funding'];
    return !nonDeletableStages.includes(pitch.stage);
  }, []);

  const deletePitch = useCallback(async (pitchId: string, userId: string) => {
    console.log('🗑️ Soft-deleting pitch:', pitchId, 'for user:', userId);
    
    const now = new Date().toISOString();
    
    setPitches(prev => prev.map(p => {
      if (p.id !== pitchId) return p;
      if (!isDeletable(p, userId)) {
        console.warn('Pitch not deletable:', p.id, p.stage);
        return p;
      }
      return { ...p, deletedAt: now, stage: 'deleted' as Stage, updatedAt: now };
    }));
    
    clearNotificationsForPitch(pitchId);
    
    try {
      const { error } = await supabase
        .from('pitches')
        .update({ deleted_at: now, stage: 'deleted', updated_at: now })
        .eq('id', pitchId);
      
      if (error) console.error('Supabase deletePitch error:', error);
      else console.log('✅ Pitch soft-delete synced to Supabase');
    } catch (err) {
      console.error('Failed to sync delete to Supabase:', err);
    }
    
    await refreshPitches();
  }, [isDeletable, clearNotificationsForPitch, refreshPitches]);

  const adminDeletePitch = useCallback(async (pitchId: string) => {
    console.log('👑 Admin soft-deleting pitch:', pitchId);
    
    const pitch = pitchesRef.current.find(p => p.id === pitchId);
    if (!pitch) {
      console.error('❌ Pitch not found:', pitchId);
      return;
    }
    
    const now = new Date().toISOString();
    
    setPitches(prev => prev.map(p =>
      p.id === pitchId
        ? { ...p, deletedAt: now, stage: 'deleted' as Stage, updatedAt: now }
        : p
    ));
    
    clearNotificationsForPitch(pitchId);
    
    if (pitch.founderId) {
      addNotification(
        pitch.founderId,
        `Your pitch "${pitch.companyName}" has been removed by an administrator.`,
        pitchId,
        undefined,
        'error'
      );
    }

    try {
      const { error } = await supabase
        .from('pitches')
        .update({ deleted_at: now, stage: 'deleted', updated_at: now })
        .eq('id', pitchId);
      
      if (error) console.error('Supabase adminDeletePitch error:', error);
      else console.log('✅ Admin pitch delete synced to Supabase');
    } catch (err) {
      console.error('Failed to sync admin delete to Supabase:', err);
    }
    
    await refreshPitches();
  }, [clearNotificationsForPitch, addNotification, refreshPitches]);

  const restorePitch = useCallback(async (pitchId: string, userId: string): Promise<boolean> => {
    console.log('🔄 Restoring pitch:', pitchId, 'for user:', userId);
    
    const now = new Date().toISOString();
    let restoredPitch: Pitch | null = null;
    
    setPitches(prev => {
      const updated = prev.map(p => {
        if (p.id === pitchId && p.founderId === userId && p.deletedAt) {
          restoredPitch = { ...p, deletedAt: undefined, stage: 'idea' as Stage, updatedAt: now };
          return restoredPitch;
        }
        return p;
      });
      return updated;
    });
    
    if (restoredPitch) {
      try {
        const { error } = await supabase
          .from('pitches')
          .update({ deleted_at: null, stage: 'idea', updated_at: now })
          .eq('id', pitchId);
        
        if (error) {
          console.error('Supabase restorePitch error:', error);
          return false;
        }
        
        addNotification(
          userId,
          `🔄 Your pitch "${restoredPitch.companyName}" has been restored.`,
          pitchId,
          'overview',
          'success'
        );
        
        await refreshPitches();
        return true;
      } catch (err) {
        console.error('Failed to restore pitch:', err);
        return false;
      }
    }
    
    return false;
  }, [addNotification, refreshPitches]);

  const permanentlyDeletePitch = useCallback(async (pitchId: string, userId: string): Promise<boolean> => {
    console.log('🗑️ PERMANENTLY DELETING pitch:', pitchId, 'for user:', userId);
    
    const pitch = pitchesRef.current.find(p => p.id === pitchId);
    
    if (!pitch) {
      console.error('❌ Pitch not found:', pitchId);
      return false;
    }
    
    if (pitch.founderId !== userId) {
      console.error('❌ Unauthorized: Pitch belongs to', pitch.founderId, 'not', userId);
      return false;
    }
    
    try {
      const { error } = await supabase.from('pitches').delete().eq('id', pitchId);
      
      if (error) {
        console.error('Supabase permanentlyDeletePitch error:', error);
        return false;
      }
      console.log('✅ Pitch permanently deleted from Supabase');
    } catch (err) {
      console.error('Failed to delete from Supabase:', err);
      return false;
    }
    
    setPitches(prev => prev.filter(p => p.id !== pitchId));
    clearNotificationsForPitch(pitchId);
    
    setMeetings(prev => {
      const newMeetings = { ...prev };
      delete newMeetings[pitchId];
      localStorage.setItem('vett_meetings', JSON.stringify(newMeetings));
      return newMeetings;
    });
    
    await refreshPitches();
    return true;
  }, [clearNotificationsForPitch, refreshPitches]);

  const createPitch = useCallback(async (data: any): Promise<string> => {
    const id = `pitch-${Date.now()}`;
    const now = new Date().toISOString();
    
    const processedData = {
      ...data,
      traction: data.traction ? {
        customerInterviews: Number(data.traction.customerInterviews) || 0,
        waitlistSignups: Number(data.traction.waitlistSignups) || 0,
        lettersOfIntent: Number(data.traction.lettersOfIntent) || 0,
        pilotCustomers: Number(data.traction.pilotCustomers) || 0,
        mvpStatus: data.traction.mvpStatus || 'none',
        mvpUrl: data.traction.mvpUrl || '',
        monthlyActiveUsers: Number(data.traction.monthlyActiveUsers) || 0,
        revenueRunRate: Number(data.traction.revenueRunRate) || 0,
      } : undefined,
      financial: data.financial ? {
        fundingAsk: Number(data.financial.fundingAsk) || 0,
        useOfFunds: {
          product: Number(data.financial.useOfFunds?.product) || 0,
          marketing: Number(data.financial.useOfFunds?.marketing) || 0,
          team: Number(data.financial.useOfFunds?.team) || 0,
          operations: Number(data.financial.useOfFunds?.operations) || 0,
          legal: Number(data.financial.useOfFunds?.legal) || 0,
          other: Number(data.financial.useOfFunds?.other) || 0,
        },
        runwayMonths: Number(data.financial.runwayMonths) || 0,
        burnRate: Number(data.financial.burnRate) || 0,
        revenueProjection: {
          month1: Number(data.financial.revenueProjection?.month1) || 0,
          month3: Number(data.financial.revenueProjection?.month3) || 0,
          month6: Number(data.financial.revenueProjection?.month6) || 0,
          month12: Number(data.financial.revenueProjection?.month12) || 0,
        },
        breakevenMonth: Number(data.financial.breakevenMonth) || 0,
      } : undefined,
    };

    const pitch: Pitch = {
      ...processedData,
      id,
      founderId: data.founderId,
      companyName: data.companyName,
      tagline: data.tagline || '',
      problem: data.problem || '',
      solution: data.solution || '',
      marketSize: data.marketSize || '',
      businessModel: data.businessModel || '',
      team: data.team || '',
      stage: 'idea',
      analysisScores: null,
      meetings: [],
      diligenceData: null,
      contractSignedFounder: false,
      contractSignedAnalyst: false,
      bootcampProgress: [],
      milestones: [],
      escrowBalance: 0,
      assignedAnalystId: null,
      assignedBySeniorId: null,
      createdAt: now,
      updatedAt: now,
    };
    
    try {
      const { error } = await supabase.from('pitches').insert(pitchToSupabase(pitch));
      
      if (error) {
        console.error('Error saving to Supabase:', error);
        setPitches(prev => [...prev, pitch]);
      } else {
        console.log('✅ Pitch saved to Supabase:', pitch.companyName);
        setPitches(prev => [...prev, pitch]);
      }
    } catch (err) {
      console.error('Failed to save to Supabase:', err);
      setPitches(prev => [...prev, pitch]);
    }
    
    clearTempPitch();
    await refreshPitches();
    triggerGlobalRefresh();
    return id;
  }, [clearTempPitch, refreshPitches, triggerGlobalRefresh]);

  const updatePitchFields = useCallback(async (id: string, fields: Partial<Pick<Pitch, 'companyName' | 'tagline' | 'problem' | 'solution' | 'marketSize' | 'businessModel' | 'team'>>) => {
    const now = new Date().toISOString();
    setPitches(prev => prev.map(p => 
      p.id === id ? { ...p, ...fields, updatedAt: now } : p
    ));
    
    const supabaseFields: Record<string, any> = { updated_at: now };
    if (fields.companyName !== undefined) supabaseFields.company_name = fields.companyName;
    if (fields.tagline !== undefined) supabaseFields.tagline = fields.tagline;
    if (fields.problem !== undefined) supabaseFields.problem = fields.problem;
    if (fields.solution !== undefined) supabaseFields.solution = fields.solution;
    if (fields.marketSize !== undefined) supabaseFields.market_size = fields.marketSize;
    if (fields.businessModel !== undefined) supabaseFields.business_model = fields.businessModel;
    if (fields.team !== undefined) supabaseFields.team = fields.team;
    
    try {
      const { error } = await supabase.from('pitches').update(supabaseFields).eq('id', id);
      if (error) console.error('Supabase updatePitchFields error:', error);
    } catch (err) {
      console.error('Failed to update pitch fields:', err);
    }
  }, []);

  const submitForAnalysis = useCallback(async (pitchId: string) => {
    const pitch = pitchesRef.current.find(p => p.id === pitchId);
    if (!pitch) return;

    if (pitch.stage !== 'idea') {
      console.warn('Pitch already analyzed or in wrong stage:', pitch.stage);
      addNotification(
        pitch.founderId,
        `Cannot analyze pitch "${pitch.companyName}" - it's already in ${pitch.stage} stage.`,
        pitchId,
        'analysis',
        'warning'
      );
      return;
    }

    try {
      const scores = await analyzePitch(pitch);
      const now = new Date().toISOString();
      
      let nextStage: Stage;
      if (scores.overall >= ANALYSIS_THRESHOLD) {
        nextStage = 'awaiting_assignment';
      } else {
        nextStage = 'analysis';
      }

      setPitches(prev => prev.map(p =>
        p.id === pitchId
          ? { ...p, analysisScores: scores, stage: nextStage, updatedAt: now }
          : p
      ));

      try {
        await supabase.from('pitches').update({
          analysis_scores: scores,
          stage: nextStage,
          updated_at: now,
        }).eq('id', pitchId);
      } catch (err) {
        console.error('Failed to save analysis to Supabase:', err);
      }

      if (scores.overall >= ANALYSIS_THRESHOLD) {
        addNotification(
          pitch.founderId, 
          `🎉 Your pitch "${pitch.companyName}" scored ${scores.overall}/100 and passed initial analysis! It will now be reviewed for assignment.`, 
          pitchId, 
          'assignment',
          'success'
        );
      } else {
        addNotification(
          pitch.founderId, 
          `Your pitch "${pitch.companyName}" scored ${scores.overall}/100. Please review the feedback and improve your pitch.`, 
          pitchId, 
          'analysis',
          'warning'
        );
      }
      
      await refreshPitches();
    } catch (error) {
      console.error('Analysis failed:', error);
      addNotification(
        pitch.founderId,
        'Analysis service temporarily unavailable. Please try again later.',
        pitchId,
        'analysis',
        'error'
      );
    }
  }, [addNotification, refreshPitches]);

  const assignPitch = useCallback(async (pitchId: string, analystId: string, seniorId?: string) => {
    const pitch = pitchesRef.current.find(p => p.id === pitchId);
    
    if (!pitch) {
      console.error('❌ Pitch not found:', pitchId);
      return;
    }
    
    if (!pitch.analysisScores || pitch.analysisScores.overall < ANALYSIS_THRESHOLD) {
      console.error('❌ Pitch cannot be assigned - has not passed AI analysis');
      addNotification(
        seniorId || 'admin',
        `Cannot assign pitch "${pitch.companyName}" - it has not passed AI analysis.`,
        pitchId,
        'assignment',
        'error'
      );
      return;
    }
    
    if (pitch.stage !== 'awaiting_assignment') {
      console.error('❌ Pitch cannot be assigned - wrong stage:', pitch.stage);
      return;
    }
    
    const now = new Date().toISOString();

    setPitches(prev => prev.map(p => {
      if (p.id !== pitchId) return p;
      
      const meeting: Meeting = {
        id: `meeting-${Date.now()}`,
        pitchId,
        agenda: generateAgenda(p),
        outcome: 'pending',
        date: new Date(Date.now() + 7 * 86400000).toISOString(),
      };
      
      clearNotificationsForPitch(pitchId, 'meeting');
      
      addNotification(
        p.founderId, 
        `📅 Your pitch "${p.companyName}" has been assigned to an analyst. A validation meeting is scheduled.`, 
        pitchId, 
        'meeting',
        'success'
      );

      return {
        ...p,
        assignedAnalystId: analystId,
        assignedBySeniorId: seniorId ?? null,
        stage: 'validation' as Stage,
        meetings: [meeting],
        updatedAt: now,
      };
    }));

    try {
      const { error } = await supabase.from('pitches').update({
        assigned_analyst_id: analystId,
        stage: 'validation',
        updated_at: now,
      }).eq('id', pitchId);
      
      if (error) console.error('Supabase assignPitch error:', error);
      else console.log('✅ Pitch assignment synced to Supabase');
    } catch (err) {
      console.error('Failed to sync assignment to Supabase:', err);
    }
    
    await refreshPitches();
  }, [addNotification, clearNotificationsForPitch, refreshPitches]);

  const saveMeeting = useCallback(async (pitchId: string, meetingData: any) => {
    setMeetings(prev => {
      const pitchMeetings = prev[pitchId] || [];
      const updated = {
        ...prev,
        [pitchId]: [...pitchMeetings, { ...meetingData, id: meetingData.id || `meeting-${Date.now()}` }]
      };
      return updated;
    });

    setPitches(prev => prev.map(p => {
      if (p.id !== pitchId) return p;
      return {
        ...p,
        meetings: [
          ...p.meetings,
          {
            id: meetingData.id || `meeting-${Date.now()}`,
            pitchId,
            agenda: meetingData.agenda || [],
            outcome: 'pending',
            date: meetingData.proposedDates?.[0]?.date || new Date().toISOString(),
          }
        ],
        updatedAt: new Date().toISOString()
      };
    }));

    addNotification(
      meetingData.founderId,
      `📅 New meeting proposal for "${pitchesRef.current.find(p => p.id === pitchId)?.companyName}"`,
      pitchId,
      'meeting',
      'info'
    );
    
    await refreshPitches();
  }, [addNotification, refreshPitches]);

  const saveMeetingNotes = useCallback(async (pitchId: string, meetingId: string, notes: any) => {
    setMeetings(prev => {
      const pitchMeetings = prev[pitchId] || [];
      const updatedMeetings = pitchMeetings.map(m => 
        m.id === meetingId ? { ...m, meetingNotes: notes, status: 'completed' } : m
      );
      return { ...prev, [pitchId]: updatedMeetings };
    });

    if (notes.recommendation === 'proceed') {
      const now = new Date().toISOString();
      setPitches(prev => prev.map(p => {
        if (p.id !== pitchId) return p;
        return { ...p, stage: 'contract', updatedAt: now };
      }));
      
      try {
        await supabase.from('pitches').update({ stage: 'contract', updated_at: now }).eq('id', pitchId);
      } catch (err) {
        console.error('Failed to update stage:', err);
      }

      addNotification(
        pitchesRef.current.find(p => p.id === pitchId)?.founderId || '',
        `✅ Meeting completed! Moving to contract stage.`,
        pitchId,
        'contract',
        'success'
      );
    }
    
    await refreshPitches();
  }, [addNotification, refreshPitches]);

  const updateMeetingStatus = useCallback(async (pitchId: string, meetingId: string, status: string) => {
    setMeetings(prev => {
      const pitchMeetings = prev[pitchId] || [];
      const updatedMeetings = pitchMeetings.map(m => 
        m.id === meetingId ? { ...m, status, updatedAt: new Date().toISOString() } : m
      );
      return { ...prev, [pitchId]: updatedMeetings };
    });

    setPitches(prev => prev.map(p => {
      if (p.id !== pitchId) return p;
      return {
        ...p,
        meetings: p.meetings.map(m => 
          m.id === meetingId ? { ...m, outcome: status === 'confirmed' ? 'approved' : status } : m
        ),
        updatedAt: new Date().toISOString()
      };
    }));
    
    await refreshPitches();
  }, [refreshPitches]);

  const getMeetingsForPitch = useCallback((pitchId: string) => {
    return meetings[pitchId] || [];
  }, [meetings]);

  const updateMeetingAgenda = useCallback(async (pitchId: string, meetingId: string, agenda: string[], founderId: string) => {
    setPitches(prev => prev.map(p =>
      p.id === pitchId
        ? { ...p, meetings: p.meetings.map(m => m.id === meetingId ? { ...m, agenda } : m), updatedAt: new Date().toISOString() }
        : p
    ));
    addNotification(
      founderId, 
      '📝 The meeting agenda has been updated. Please review.', 
      pitchId, 
      'meeting',
      'info'
    );
    await refreshPitches();
  }, [addNotification, refreshPitches]);

  const approveMeeting = useCallback(async (pitchId: string, meetingId: string) => {
    const now = new Date().toISOString();
    setPitches(prev => prev.map(p => {
      if (p.id !== pitchId) return p;
      clearNotificationsForPitch(pitchId, 'meeting');
      addNotification(
        p.founderId, 
        `✅ Meeting approved! Please review and sign the contract.`, 
        pitchId, 
        'contract',
        'success'
      );
      return {
        ...p,
        stage: 'contract' as Stage,
        meetings: p.meetings.map(m => m.id === meetingId ? { ...m, outcome: 'approved' as const } : m),
        updatedAt: now,
      };
    }));
    await updateMeetingStatus(pitchId, meetingId, 'confirmed');
    
    try {
      await supabase.from('pitches').update({ stage: 'contract', updated_at: now }).eq('id', pitchId);
    } catch (err) {
      console.error('Failed to update stage:', err);
    }
    
    await refreshPitches();
  }, [addNotification, clearNotificationsForPitch, updateMeetingStatus, refreshPitches]);

  const rejectMeeting = useCallback(async (pitchId: string, meetingId: string) => {
    setPitches(prev => prev.map(p => {
      if (p.id !== pitchId) return p;
      addNotification(
        p.founderId, 
        `❌ Your meeting was not approved. Please check feedback.`, 
        pitchId, 
        'meeting',
        'error'
      );
      return { 
        ...p, 
        meetings: p.meetings.map(m => m.id === meetingId ? { ...m, outcome: 'rejected' as const } : m),
        updatedAt: new Date().toISOString(),
      };
    }));
    await updateMeetingStatus(pitchId, meetingId, 'cancelled');
    await refreshPitches();
  }, [addNotification, updateMeetingStatus, refreshPitches]);

  const updatePitchStage = useCallback(async (pitchId: string, newStage: Stage) => {
    const now = new Date().toISOString();
    setPitches(prev => prev.map(p => {
      if (p.id !== pitchId) return p;
      addNotification(
        p.founderId,
        `🔄 Pitch moved to ${newStage} stage`,
        pitchId,
        newStage === 'validation' ? 'meeting' : 
        newStage === 'contract' ? 'contract' :
        newStage === 'bootcamp' ? 'bootcamp' :
        newStage === 'diligence' ? 'diligence' :
        newStage === 'funding' ? 'funding' : 'overview',
        'info'
      );
      return { ...p, stage: newStage, updatedAt: now };
    }));
    
    try {
      await supabase.from('pitches').update({ stage: newStage, updated_at: now }).eq('id', pitchId);
    } catch (err) {
      console.error('Failed to update stage:', err);
    }
    
    await refreshPitches();
  }, [addNotification, refreshPitches]);

  const submitDiligence = useCallback(async (pitchId: string, data: Omit<DiligenceData, 'verified'>) => {
    const now = new Date().toISOString();
    setPitches(prev => prev.map(p =>
      p.id === pitchId 
        ? { ...p, diligenceData: { ...data, verified: false }, updatedAt: now }
        : p
    ));
    
    try {
      await supabase.from('pitches').update({ diligence_data: { ...data, verified: false }, updated_at: now }).eq('id', pitchId);
    } catch (err) {
      console.error('Failed to save diligence data:', err);
    }
    
    await refreshPitches();
  }, [refreshPitches]);

  const verifyDiligence = useCallback(async (pitchId: string) => {
    const now = new Date().toISOString();
    setPitches(prev => prev.map(p => {
      if (p.id !== pitchId || !p.diligenceData) return p;
      clearNotificationsForPitch(pitchId, 'diligence');
      addNotification(
        p.founderId, 
        `🎓 Due diligence complete! Welcome to the bootcamp.`, 
        pitchId, 
        'bootcamp',
        'success'
      );
      return { ...p, diligenceData: { ...p.diligenceData, verified: true }, stage: 'bootcamp' as Stage, updatedAt: now };
    }));
    
    try {
      await supabase.from('pitches').update({ stage: 'bootcamp', updated_at: now }).eq('id', pitchId);
    } catch (err) {
      console.error('Failed to update stage:', err);
    }
    
    await refreshPitches();
  }, [addNotification, clearNotificationsForPitch, refreshPitches]);

  const signContract = useCallback(async (pitchId: string, signer: 'founder' | 'analyst') => {
    const now = new Date().toISOString();
    setPitches(prev => prev.map(p => {
      if (p.id !== pitchId) return p;
      const updated = { ...p };
      if (signer === 'founder') updated.contractSignedFounder = true;
      if (signer === 'analyst') updated.contractSignedAnalyst = true;
      
      if (updated.contractSignedFounder && updated.contractSignedAnalyst) {
        clearNotificationsForPitch(pitchId, 'contract');
        updated.stage = 'bootcamp';
        updated.bootcampProgress = generateBootcampDays();
        updated.milestones = JSON.parse(JSON.stringify(DEFAULT_MILESTONES));
        updated.escrowBalance = DEFAULT_MILESTONES.reduce((s, m) => s + m.trancheAmount, 0);
        updated.updatedAt = now;
        addNotification(
          p.founderId, 
          `📝 Contract signed! Starting bootcamp.`, 
          pitchId, 
          'bootcamp',
          'success'
        );
        
        supabase.from('pitches').update({
          contract_signed_founder: true,
          contract_signed_analyst: true,
          stage: 'bootcamp',
          updated_at: now,
        }).eq('id', pitchId).then(({ error }) => {
          if (error) console.error('Supabase signContract (both) error:', error);
        });
      } else {
        const updateData: Record<string, any> = { updated_at: now };
        if (signer === 'founder') updateData.contract_signed_founder = true;
        if (signer === 'analyst') updateData.contract_signed_analyst = true;
        supabase.from('pitches').update(updateData).eq('id', pitchId)
          .then(({ error }) => { if (error) console.error('Supabase signContract error:', error); });
      }
      return updated;
    }));
    await refreshPitches();
  }, [addNotification, clearNotificationsForPitch, refreshPitches]);

  const toggleBootcampDay = useCallback(async (pitchId: string, dayIndex: number) => {
    setPitches(prev => prev.map(p => {
      if (p.id !== pitchId) return p;
      const progress = p.bootcampProgress.map((d, i) =>
        i === dayIndex ? { ...d, completed: !d.completed } : d
      );
      const allDone = progress.every(d => d.completed);
      if (allDone && p.stage === 'bootcamp') {
        clearNotificationsForPitch(pitchId, 'bootcamp');
        addNotification(
          p.founderId, 
          `🎉 Bootcamp completed! Waiting for analyst verification.`, 
          pitchId, 
          'bootcamp',
          'success'
        );
      }
      return { ...p, bootcampProgress: progress, updatedAt: new Date().toISOString() };
    }));
    await refreshPitches();
  }, [addNotification, clearNotificationsForPitch, refreshPitches]);

  const completeMilestone = useCallback(async (pitchId: string, milestoneId: string) => {
    setPitches(prev => prev.map(p =>
      p.id === pitchId
        ? { ...p, milestones: p.milestones.map(m => m.id === milestoneId ? { ...m, completed: true } : m), updatedAt: new Date().toISOString() }
        : p
    ));
    await refreshPitches();
  }, [refreshPitches]);

  const approveMilestone = useCallback(async (pitchId: string, milestoneId: string) => {
    setPitches(prev => prev.map(p => {
      if (p.id !== pitchId) return p;
      const milestones = p.milestones.map(m =>
        m.id === milestoneId ? { ...m, approved: true } : m
      );
      const ms = milestones.find(m => m.id === milestoneId);
      const newEscrow = ms ? p.escrowBalance - ms.trancheAmount : p.escrowBalance;
      addNotification(
        p.founderId, 
        `💰 Milestone approved! $${ms?.trancheAmount?.toLocaleString()} released.`, 
        pitchId, 
        'funding',
        'success'
      );
      return { ...p, milestones, escrowBalance: Math.max(0, newEscrow), updatedAt: new Date().toISOString() };
    }));
    await refreshPitches();
  }, [addNotification, refreshPitches]);

  const markNotificationRead = useCallback((notificationId: string) => {
    setNotifications(prev => prev.map(n =>
      n.id === notificationId ? { ...n, read: true } : n
    ));
  }, []);

  const markAllNotificationsRead = useCallback((userId: string) => {
    setNotifications(prev => prev.map(n =>
      n.userId === userId ? { ...n, read: true } : n
    ));
  }, []);

  const getNotificationsForUser = useCallback((userId: string) => {
    return notifications.filter(n => n.userId === userId);
  }, [notifications]);

  const getUnreadCount = useCallback((userId: string) => {
    return notifications.filter(n => n.userId === userId && !n.read).length;
  }, [notifications]);

  return (
    <DataContext.Provider value={{
      pitches, 
      notifications,
      globalRefreshKey,
      triggerGlobalRefresh,
      createPitch, 
      updatePitchFields, 
      submitForAnalysis,
      assignPitch, 
      updateMeetingAgenda, 
      approveMeeting, 
      rejectMeeting,
      saveMeeting,
      saveMeetingNotes,
      updateMeetingStatus,
      getMeetingsForPitch,
      submitDiligence, 
      verifyDiligence, 
      signContract, 
      toggleBootcampDay,
      completeMilestone, 
      approveMilestone, 
      markNotificationRead,
      markAllNotificationsRead,
      clearNotificationsForPitch,
      getNotificationsForUser,
      deletePitch,
      adminDeletePitch,
      restorePitch,
      permanentlyDeletePitch,
      isDeletable,
      getUnreadCount,
      saveTempPitch,
      getTempPitch,
      clearTempPitch,
      updatePitchStage,
      refreshPitches,
      isSupabaseConnected,
    }}>
      {children}
    </DataContext.Provider>
  );
}